import { useId, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageShell";
import { CTASection } from "@/components/site/CTASection";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { BUSINESS } from "@/components/site/constants";
import { Phone } from "lucide-react";

const TOOLS = [
  {
    id: "design-studio",
    label: "Design Studio",
    url: "https://www.atlasroofing.com/1.0/AWI/inspiration/266a01922ec1fe8e1acb812dc6a5cf5d",
    title: "Atlas Roofing Design Studio home roof color visualizer",
    description: "Upload a photo of your home and explore different Atlas shingle colors on it.",
  },
  {
    id: "pinnacle-pristine",
    label: "Pinnacle Pristine Colors",
    url: "https://www.atlasroofing.com/1.0/AWI/pristine/266a01922ec1fe8e1acb812dc6a5cf5d",
    title: "Atlas Pinnacle Pristine shingle color selector",
    description: "Browse and compare the full Atlas Pinnacle Pristine color line.",
  },
  {
    id: "pinnacle-sun",
    label: "Pinnacle Sun Colors",
    url: "https://www.atlasroofing.com/1.0/AWI/sun/266a01922ec1fe8e1acb812dc6a5cf5d",
    title: "Atlas Pinnacle Sun shingle color selector",
    description: "Browse and compare Atlas Pinnacle Sun colors — a reflective-granule shingle option.",
  },
] as const;

export const Route = createFileRoute("/atlas-shingle-colors")({
  head: () => ({
    meta: [
      { title: "Atlas Shingle Colors & Roof Visualizer | Citrus County, FL" },
      { name: "description", content: "Upload a home photo, explore Atlas shingle colors and compare Pinnacle Pristine and Pinnacle Sun options before planning your new roof." },
      { property: "og:title", content: "Atlas Shingle Colors & Roof Visualizer | Citrus County, FL" },
      { property: "og:description", content: "Preview Atlas shingle colors on a photo of your home before choosing a new roof." },
      { property: "og:url", content: canonical("/atlas-shingle-colors") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/atlas-shingle-colors") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([{ label: "Atlas Shingle Colors", href: "/atlas-shingle-colors" }]),
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          url: canonical("/atlas-shingle-colors"),
          name: "Atlas Shingle Colors & Roof Visualizer",
          about: { "@id": ROOFING_CONTRACTOR_ID },
        },
      ]),
    }],
  }),
  component: Page,
});

function Page() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const tabsId = useId();
  const active = TOOLS.find(t => t.id === activeId);

  return (
    <>
      <Breadcrumbs items={[{ label: "Atlas Shingle Colors", href: "/atlas-shingle-colors" }]} />
      <PageHero
        eyebrow="Roof Color Visualizer"
        title="Preview Atlas Shingle Colors on Your Home"
        subtitle="Destination Roofing is an Atlas Certified Scotchgard™ Shingle Sales Specialist. Use the free Atlas tools below to explore shingle colors before your roof replacement."
      />
      <section className="mx-auto max-w-6xl px-4 py-12 md:py-16">
        <div className="prose prose-slate max-w-none mb-8">
          <p>
            Destination Roofing's preferred shingle brand is Atlas Roofing. The visualizer lets homeowners upload a photo of their home and preview different shingle colors, and separate tools let you explore the Atlas Pinnacle Pristine and Pinnacle Sun product lines.
          </p>
          <p>
            <strong>Digital colors are an approximation.</strong> Final color selection should be made using current physical shingle samples and confirmed product availability. Product availability, warranties, wind ratings, impact ratings and color options can change — we'll walk you through what's currently available for your Florida home during your <Link to="/contact">Free Roof Inspection & Estimate</Link>.
          </p>
          <p>
            Learn more about <Link to="/shingle-roofing">Atlas shingle roofing</Link> and <Link to="/roof-replacement">roof replacement</Link>. If you're comparing materials, our article on{" "}
            <Link to="/blog/shingle-metal-flat-roofing-citrus-county">shingle vs. metal vs. flat roofing in Citrus County</Link> may help.
          </p>
        </div>

        {/* Tabs */}
        <div className="rounded-xl border border-border bg-white overflow-hidden shadow-[var(--shadow-card)]">
          <div role="tablist" aria-label="Atlas shingle visualizer tools" className="flex flex-col sm:flex-row border-b border-border">
            {TOOLS.map(t => (
              <button
                key={t.id}
                role="tab"
                id={`${tabsId}-tab-${t.id}`}
                aria-selected={activeId === t.id}
                aria-controls={`${tabsId}-panel`}
                onClick={() => { setActiveId(t.id); setLoaded(false); }}
                className={`flex-1 px-5 py-4 text-sm font-semibold uppercase tracking-wider text-left border-b sm:border-b-0 sm:border-r border-border last:border-r-0 last:border-b-0 transition-colors ${
                  activeId === t.id ? "bg-brand text-brand-foreground" : "bg-white text-foreground hover:bg-brand/5"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div
            id={`${tabsId}-panel`}
            role="tabpanel"
            aria-labelledby={activeId ? `${tabsId}-tab-${activeId}` : undefined}
            className="p-4 md:p-6"
          >
            {!active && (
              <div className="text-center py-16">
                <p className="text-lg font-display font-bold">Choose a tool above to start</p>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                  Loading these tools uses your data connection, so we only load the one you select. Pick <em>Design Studio</em> to upload a photo of your home, or one of the color selectors to browse shingles.
                </p>
              </div>
            )}
            {active && (
              <div>
                <div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="font-display font-bold text-lg">{active.label}</h2>
                    <p className="text-sm text-muted-foreground mt-1">{active.description}</p>
                  </div>
                  <a
                    href={active.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-brand underline"
                  >
                    Open on Atlas Roofing ↗
                  </a>
                </div>
                <div className="relative rounded-lg overflow-hidden border border-border bg-slate-50" style={{ minHeight: 1200 }}>
                  {!loaded && (
                    <div className="absolute inset-0 grid place-items-center text-sm text-muted-foreground">
                      Loading the {active.label} tool from Atlas Roofing…
                    </div>
                  )}
                  <iframe
                    key={active.id}
                    title={active.title}
                    src={active.url}
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                    width="100%"
                    style={{ minHeight: 1200, border: 0, display: "block" }}
                    onLoad={() => setLoaded(true)}
                    id={`atlas-${active.id}`}
                  />
                </div>
                <noscript>
                  <p className="mt-4 text-sm">
                    The visualizer requires JavaScript.{" "}
                    <a href={active.url} target="_blank" rel="noopener noreferrer" className="underline">
                      Open {active.label} on the Atlas Roofing website
                    </a>.
                  </p>
                </noscript>
              </div>
            )}
          </div>
        </div>

        {/* CTA under tool */}
        <div className="mt-10 rounded-xl border-2 border-brand bg-brand/5 p-6 md:p-8 text-center">
          <h2 className="font-display font-bold text-2xl">Request an Atlas Shingle Roof Estimate</h2>
          <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
            Ready to move from color samples to a written estimate? We'll come out, walk your roof and give you honest options for an Atlas shingle roof replacement.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={BUSINESS.phoneHref} className="btn-cta"><Phone className="size-4" /> Call {BUSINESS.phone}</a>
            <Link to="/contact" className="btn-outline-light !bg-white !text-brand !border-brand">Schedule my Free Roof Inspection</Link>
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}
